import translate from '@translate';
import React, { FC, useState, useEffect } from 'react';
import Grid, { GridItem } from 'src/components/Grid';
import TextField from 'src/components/TextField';
import { ProviderType } from 'src/types/provider';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import Select from 'src/components/Select';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { EditKeysType } from './constants';
import { Typography, Box } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import FileUpload, { CustomFileActions, FilesPropType } from 'src/components/FileUpload';
import { FileType } from 'src/types/file';
import { useCheckPermission } from 'src/utils/checkPermission';
import { useSelector } from 'react-redux';
import { RootStoreType } from 'src/types/store';
import api from '@api';
import Tooltip from 'src/components/Tooltip';
import { styled } from '@mui/material/styles';

export interface ContractPropsType {
    onChange: OnChangeType<EditKeysType>;
    editData: ProviderType;
    editErrors: FormErrorsType;
    disableEdit: boolean;
    contractTermCodes: ConstantsCodeType[];
    permissionFiles: boolean;
}

const InfoIconStyled = styled('span')({
    marginLeft: '10px',

    '& svg': {
        width: '14px',
        height: '14px',
        cursor: 'pointer'
    }
});


const Contract: FC<ContractPropsType> = (props) => {
    const [files, setFiles] = useState<FileType[]>([]);

    const providerFileTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.providerFileTypeCodes) || [];

    useEffect(() => {
        setFiles(props.editData.FILES || []);
    }, [props.editData.FILES]);

    const handleChange = (value: FilesPropType, error: boolean) => {
        props.onChange('FILES', value.map((file) => ({ ...file, type: file.type || 'providerContract' })), error);
    };

    const handleChangeType = (value: any, error: boolean, inputFile: FileType, inputName: string) => {
        props.onChange(
            'FILES',
            files.map((file) => (file === inputFile ? { ...file, type: value } : file)),
            error,
            inputName
        );
    };

    const customFileActions: CustomFileActions = (inputFile) => {
        const inputName = `file-type-${inputFile.fileName}`;
        return (
            <Select
                options={providerFileTypeCodes}
                value={inputFile.type}
                error={props.editErrors[inputName]}
                onChange={(value, error) => handleChangeType(value, error, inputFile, inputName)}
                name={inputName}
                required
                fullWidth
                disabled={props.disableEdit || !props.permissionFiles}
            />
        );
    };

    const showEndDateField = props.editData.CONTRACT_TERM === 'specificDate';

    return (
        <>
            {/* Contract with client section */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    {translate('providerDetail.contractWithClient')}
                </Typography>
                <Grid>
                    <GridItem cols={3}>
                        <TextField
                            value={props.editData.CONTRACT_SIGNING_DATE}
                            error={props.editErrors.CONTRACT_SIGNING_DATE}
                            onChange={(value, error) => props.onChange('CONTRACT_SIGNING_DATE', value, error)}
                            name="CONTRACT_SIGNING_DATE"
                            label={translate('providerDetail.contractSigningDate')}
                            fullWidth
                            disabled={props.disableEdit}
                            type="date"
                            required
                        />
                    </GridItem>
                    <GridItem cols={3}>
                        <Select
                            options={props.contractTermCodes}
                            value={props.editData.CONTRACT_TERM}
                            error={props.editErrors.CONTRACT_TERM}
                            onChange={(value, error) => props.onChange('CONTRACT_TERM', value, error)}
                            name="CONTRACT_TERM"
                            label={translate('providerDetail.contractTerm')}
                            fullWidth
                            disabled={props.disableEdit}
                            required
                        />
                        {showEndDateField && (
                            // <GridItem cols={4}>
                            <TextField
                                value={props.editData.CONTRACT_END_DATE}
                                error={props.editErrors.CONTRACT_END_DATE}
                                onChange={(value, error) => props.onChange('CONTRACT_END_DATE', value, error)}
                                name="CONTRACT_END_DATE"
                                label={translate('providerDetail.contractEndDate')}
                                fullWidth
                                required={showEndDateField}
                                disabled={props.disableEdit}
                                type="date"
                            />
                            // </GridItem>
                        )}
                    </GridItem>

                    <GridItem cols={3}>
                        <TextField
                            value={props.editData.INVOICE_DUE_DAYS}
                            error={props.editErrors.INVOICE_DUE_DAYS}
                            onChange={(value, error) => props.onChange('INVOICE_DUE_DAYS', value || null, error)}
                            name="INVOICE_DUE_DAYS"
                            label={translate('providerDetail.invoiceDueDays')}
                            fullWidth
                            disabled={props.disableEdit}
                            type="numberLocale"
                            min={0}
                            max={365}
                            required
                        />
                    </GridItem>
                    <GridItem cols={3}>
                        <TextField
                            value={props.editData.NOTICE_PERIOD_MONTHS}
                            error={props.editErrors.NOTICE_PERIOD_MONTHS}
                            onChange={(value, error) => props.onChange('NOTICE_PERIOD_MONTHS', value || null, error)}
                            name="NOTICE_PERIOD_MONTHS"
                            label={translate('providerDetail.noticePeriodMonths')}
                            fullWidth
                            disabled={props.disableEdit}
                            type="numberLocale"
                            min={0}
                            max={100}
                            required
                        />
                    </GridItem>
                </Grid>
            </Box>

            {/* Quality level and compensation section */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {translate('providerDetail.qualityLevelAndCompensation')}
                </Typography>

                <Grid>
                    {/* Receipt of products section */}
                    <GridItem cols={3}>
                        <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                            {translate('providerDetail.receiptProductsDiscount')}
                            <Tooltip title={translate('providerDetail.receiptProductsDiscount.tooltip')}>
                                <InfoIconStyled>
                                    <InfoIcon sx={{ fontSize: 16 }} />
                                </InfoIconStyled>
                            </Tooltip>
                        </Typography>
                        <Grid>
                            <GridItem cols={12}>
                                <TextField
                                    value={props.editData.RECEIPT_DISCOUNT_12_24H}
                                    error={props.editErrors.RECEIPT_DISCOUNT_12_24H}
                                    onChange={(value, error) => props.onChange('RECEIPT_DISCOUNT_12_24H', value || null, error)}
                                    name="RECEIPT_DISCOUNT_12_24H"
                                    label={translate('providerDetail.hours12to24')}
                                    fullWidth
                                    disabled={props.disableEdit}
                                    type="numberLocale"
                                    min={0}
                                    max={100}
                                    endAdornment="%"
                                />
                            </GridItem>
                            <GridItem cols={12}>
                                <TextField
                                    value={props.editData.RECEIPT_DISCOUNT_24_48H}
                                    error={props.editErrors.RECEIPT_DISCOUNT_24_48H}
                                    onChange={(value, error) => props.onChange('RECEIPT_DISCOUNT_24_48H', value || null, error)}
                                    name="RECEIPT_DISCOUNT_24_48H"
                                    label={translate('providerDetail.hours24to48')}
                                    fullWidth
                                    disabled={props.disableEdit}
                                    type="numberLocale"
                                    min={0}
                                    max={100}
                                    endAdornment="%"
                                />
                            </GridItem>
                            <GridItem cols={12}>
                                <TextField
                                    value={props.editData.RECEIPT_DISCOUNT_48_72H}
                                    error={props.editErrors.RECEIPT_DISCOUNT_48_72H}
                                    onChange={(value, error) => props.onChange('RECEIPT_DISCOUNT_48_72H', value || null, error)}
                                    name="RECEIPT_DISCOUNT_48_72H"
                                    label={translate('providerDetail.hours48to72')}
                                    fullWidth
                                    disabled={props.disableEdit}
                                    type="numberLocale"
                                    min={0}
                                    max={100}
                                    endAdornment="%"
                                />
                            </GridItem>
                            <GridItem cols={12}>
                                <TextField
                                    value={props.editData.RECEIPT_DISCOUNT_72H_PLUS}
                                    error={props.editErrors.RECEIPT_DISCOUNT_72H_PLUS}
                                    onChange={(value, error) => props.onChange('RECEIPT_DISCOUNT_72H_PLUS', value || null, error)}
                                    name="RECEIPT_DISCOUNT_72H_PLUS"
                                    label={translate('providerDetail.hours72Plus')}
                                    fullWidth
                                    disabled={props.disableEdit}
                                    type="numberLocale"
                                    min={0}
                                    max={100}
                                    endAdornment="%"
                                />
                            </GridItem>
                        </Grid>
                    </GridItem>

                    {/* Return receipt section */}
                    <GridItem cols={3}>
                        <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                            {translate('providerDetail.returnReceiptDiscount')}
                            <Tooltip title={translate('providerDetail.returnReceiptDiscount.tooltip')}>
                                <InfoIconStyled>
                                    <InfoIcon sx={{ fontSize: 16 }} />
                                </InfoIconStyled>
                            </Tooltip>
                        </Typography>
                        <Grid>
                            <GridItem cols={12}>
                                <TextField
                                    value={props.editData.RETURN_RECEIPT_DISCOUNT_12_24H}
                                    error={props.editErrors.RETURN_RECEIPT_DISCOUNT_12_24H}
                                    onChange={(value, error) => props.onChange('RETURN_RECEIPT_DISCOUNT_12_24H', value || null, error)}
                                    name="RETURN_RECEIPT_DISCOUNT_12_24H"
                                    label={translate('providerDetail.hours12to24')}
                                    fullWidth
                                    disabled={props.disableEdit}
                                    type="numberLocale"
                                    min={0}
                                    max={100}
                                    endAdornment="%"
                                />
                            </GridItem>
                            <GridItem cols={12}>
                                <TextField
                                    value={props.editData.RETURN_RECEIPT_DISCOUNT_24_48H}
                                    error={props.editErrors.RETURN_RECEIPT_DISCOUNT_24_48H}
                                    onChange={(value, error) => props.onChange('RETURN_RECEIPT_DISCOUNT_24_48H', value || null, error)}
                                    name="RETURN_RECEIPT_DISCOUNT_24_48H"
                                    label={translate('providerDetail.hours24to48')}
                                    fullWidth
                                    disabled={props.disableEdit}
                                    type="numberLocale"
                                    min={0}
                                    max={100}
                                    endAdornment="%"
                                />
                            </GridItem>
                            <GridItem cols={12}>
                                <TextField
                                    value={props.editData.RETURN_RECEIPT_DISCOUNT_48_72H}
                                    error={props.editErrors.RETURN_RECEIPT_DISCOUNT_48_72H}
                                    onChange={(value, error) => props.onChange('RETURN_RECEIPT_DISCOUNT_48_72H', value || null, error)}
                                    name="RETURN_RECEIPT_DISCOUNT_48_72H"
                                    label={translate('providerDetail.hours48to72')}
                                    fullWidth
                                    disabled={props.disableEdit}
                                    type="numberLocale"
                                    min={0}
                                    max={100}
                                    endAdornment="%"
                                />
                            </GridItem>
                            <GridItem cols={12}>
                                <TextField
                                    value={props.editData.RETURN_RECEIPT_DISCOUNT_72H_PLUS}
                                    error={props.editErrors.RETURN_RECEIPT_DISCOUNT_72H_PLUS}
                                    onChange={(value, error) => props.onChange('RETURN_RECEIPT_DISCOUNT_72H_PLUS', value || null, error)}
                                    name="RETURN_RECEIPT_DISCOUNT_72H_PLUS"
                                    label={translate('providerDetail.hours72Plus')}
                                    fullWidth
                                    disabled={props.disableEdit}
                                    type="numberLocale"
                                    min={0}
                                    max={100}
                                    endAdornment="%"
                                />
                            </GridItem>
                        </Grid>
                    </GridItem>

                    {/* B2C Dispatch section */}
                    <GridItem cols={3}>
                        <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                            {translate('providerDetail.b2cDispatchDiscount')}
                            <Tooltip title={translate('providerDetail.b2cDispatchDiscount.tooltip')}>
                                <InfoIconStyled>
                                    <InfoIcon sx={{ fontSize: 16 }} />
                                </InfoIconStyled>
                            </Tooltip>
                        </Typography>
                        <Grid>
                            <GridItem cols={12}>
                                <TextField
                                    value={props.editData.B2C_DISPATCH_DISCOUNT_97_95}
                                    error={props.editErrors.B2C_DISPATCH_DISCOUNT_97_95}
                                    onChange={(value, error) => props.onChange('B2C_DISPATCH_DISCOUNT_97_95', value || null, error)}
                                    name="B2C_DISPATCH_DISCOUNT_97_95"
                                    label={translate('providerDetail.percent9795')}
                                    fullWidth
                                    disabled={props.disableEdit}
                                    type="numberLocale"
                                    min={0}
                                    max={100}
                                    endAdornment="%"
                                />
                            </GridItem>
                            <GridItem cols={12}>
                                <TextField
                                    value={props.editData.B2C_DISPATCH_DISCOUNT_94_90}
                                    error={props.editErrors.B2C_DISPATCH_DISCOUNT_94_90}
                                    onChange={(value, error) => props.onChange('B2C_DISPATCH_DISCOUNT_94_90', value || null, error)}
                                    name="B2C_DISPATCH_DISCOUNT_94_90"
                                    label={translate('providerDetail.percent9490')}
                                    fullWidth
                                    disabled={props.disableEdit}
                                    type="numberLocale"
                                    min={0}
                                    max={100}
                                    endAdornment="%"
                                />
                            </GridItem>
                            <GridItem cols={12}>
                                <TextField
                                    value={props.editData.B2C_DISPATCH_DISCOUNT_89_LESS}
                                    error={props.editErrors.B2C_DISPATCH_DISCOUNT_89_LESS}
                                    onChange={(value, error) => props.onChange('B2C_DISPATCH_DISCOUNT_89_LESS', value || null, error)}
                                    name="B2C_DISPATCH_DISCOUNT_89_LESS"
                                    label={translate('providerDetail.percent89Less')}
                                    fullWidth
                                    disabled={props.disableEdit}
                                    type="numberLocale"
                                    min={0}
                                    max={100}
                                    endAdornment="%"
                                />
                            </GridItem>
                        </Grid>
                    </GridItem>

                    {/* B2B Dispatch section */}
                    <GridItem cols={3}>
                        <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                            {translate('providerDetail.b2bDispatchDiscount')}
                            <Tooltip title={translate('providerDetail.b2bDispatchDiscount.tooltip')}>
                                <InfoIconStyled>
                                    <InfoIcon sx={{ fontSize: 16 }} />
                                </InfoIconStyled>
                            </Tooltip>
                        </Typography>
                        <Grid>
                            <GridItem cols={12}>
                                <TextField
                                    value={props.editData.B2B_DISPATCH_DISCOUNT_0_100}
                                    error={props.editErrors.B2B_DISPATCH_DISCOUNT_0_100}
                                    onChange={(value, error) => props.onChange('B2B_DISPATCH_DISCOUNT_0_100', value || null, error)}
                                    name="B2B_DISPATCH_DISCOUNT_0_100"
                                    label={translate('providerDetail.products0100')}
                                    fullWidth
                                    disabled={props.disableEdit}
                                    type="numberLocale"
                                    min={0}
                                    max={100}
                                    endAdornment="%"
                                />
                            </GridItem>
                            <GridItem cols={12}>
                                <TextField
                                    value={props.editData.B2B_DISPATCH_DISCOUNT_101_999}
                                    error={props.editErrors.B2B_DISPATCH_DISCOUNT_101_999}
                                    onChange={(value, error) => props.onChange('B2B_DISPATCH_DISCOUNT_101_999', value || null, error)}
                                    name="B2B_DISPATCH_DISCOUNT_101_999"
                                    label={translate('providerDetail.products101999')}
                                    fullWidth
                                    disabled={props.disableEdit}
                                    type="numberLocale"
                                    min={0}
                                    max={100}
                                    endAdornment="%"
                                />
                            </GridItem>
                            <GridItem cols={12}>
                                <TextField
                                    value={props.editData.B2B_DISPATCH_DISCOUNT_1000_4000}
                                    error={props.editErrors.B2B_DISPATCH_DISCOUNT_1000_4000}
                                    onChange={(value, error) => props.onChange('B2B_DISPATCH_DISCOUNT_1000_4000', value || null, error)}
                                    name="B2B_DISPATCH_DISCOUNT_1000_4000"
                                    label={translate('providerDetail.products10004000')}
                                    fullWidth
                                    disabled={props.disableEdit}
                                    type="numberLocale"
                                    min={0}
                                    max={100}
                                    endAdornment="%"
                                />
                            </GridItem>
                            <GridItem cols={12}>
                                <TextField
                                    value={props.editData.B2B_DISPATCH_DISCOUNT_4001_PLUS}
                                    error={props.editErrors.B2B_DISPATCH_DISCOUNT_4001_PLUS}
                                    onChange={(value, error) => props.onChange('B2B_DISPATCH_DISCOUNT_4001_PLUS', value || null, error)}
                                    name="B2B_DISPATCH_DISCOUNT_4001_PLUS"
                                    label={translate('providerDetail.products4001Plus')}
                                    fullWidth
                                    disabled={props.disableEdit}
                                    type="numberLocale"
                                    min={0}
                                    max={100}
                                    endAdornment="%"
                                />
                            </GridItem>
                        </Grid>
                    </GridItem>
                </Grid>
            </Box>

            {/* Loss and Error Rate section */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {translate('providerDetail.lossAndErrorRate')}
                </Typography>
                <Grid>
                    <GridItem cols={3}>
                        <TextField
                            value={props.editData.ACCEPTABLE_LOSS_PERCENT}
                            error={props.editErrors.ACCEPTABLE_LOSS_PERCENT}
                            onChange={(value, error) => props.onChange('ACCEPTABLE_LOSS_PERCENT', value || null, error)}
                            name="ACCEPTABLE_LOSS_PERCENT"
                            label={translate('providerDetail.acceptableLossPercent')}
                            fullWidth
                            disabled={props.disableEdit}
                            type="numberLocale"
                            min={0}
                            max={100}
                            step="0.01"
                            endAdornment="%"
                        />
                    </GridItem>
                    <GridItem cols={3}>
                        <TextField
                            value={props.editData.ERROR_RATE_PERCENT}
                            error={props.editErrors.ERROR_RATE_PERCENT}
                            onChange={(value, error) => props.onChange('ERROR_RATE_PERCENT', value || null, error)}
                            name="ERROR_RATE_PERCENT"
                            label={translate('providerDetail.errorRatePercent')}
                            fullWidth
                            disabled={props.disableEdit}
                            type="numberLocale"
                            min={0}
                            max={100}
                            step="0.01"
                            endAdornment="%"
                        />
                    </GridItem>
                </Grid>
            </Box>
            {/* Documents section */}
            {props.permissionFiles && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {translate('providerFiles.title')}
                    </Typography>
                    <FileUpload
                        files={files}
                        onChange={(value, error) => handleChange(value, error)}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                        name="FILES"
                        error={props.editErrors.FILES}
                        height={130}
                        disabled={props.disableEdit}
                        multiple
                        displayFilesList
                        customFileActions={customFileActions}
                    />
                </Box>
            )}
        </>
    );
};

export default Contract;
